import { useState } from 'react';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import Modal from '../../components/common/Modal';
import styles from './RolesPage.module.css';

const EMPTY_INVITE = { firstName: '', lastName: '', email: '' };

// Every outcome of POST /admin/roles/:id/users, rendered INLINE in the modal
// rather than as a toast, so the admin can act on it (assign / reclaim / retry)
// without re-opening the form. Backend contract:
//   409 { code: 'EMAIL_EXISTS' | 'ALREADY_IN_ROLE' | 'RECLAIMABLE' }
//   201 { emailSent: false }  <- created, but the invite email did NOT go out
const OUTCOME = {
  success: { cls: 'inviteAlertSuccess', title: 'Invitation sent' },
  delivery: { cls: 'inviteAlertWarning', title: 'Invitation email not delivered' },
  exists: { cls: 'inviteAlertWarning', title: 'Account already exists' },
  inRole: { cls: 'inviteAlertWarning', title: 'Already in this role' },
  reclaim: { cls: 'inviteAlertWarning', title: 'Account can be reclaimed' },
  error: { cls: 'inviteAlertError', title: 'Could not send invitation' }
};

// Map a thrown api.js error (fetch-based: .message/.status/.code/.data) to an
// outcome. RECLAIMABLE is a conflict the admin can RESOLVE, not a failure: the
// address belongs to an orphan row that holds no role at all, so it gets a named
// confirm prompt instead of falling through to the generic error branch — which
// is what titled it "Could not send invitation" next to a useless "Try Again".
// The descriptor's existingUserId is what the reclaim is addressed to; without
// it there is nothing to confirm, so that (contract-breaking) shape still falls
// through rather than posting to an undefined id.
const classifyError = (err) => {
  if (err?.code === 'RECLAIMABLE' && err?.data?.existingUserId) {
    return {
      kind: 'reclaim',
      message: err.message,
      userId: err.data.existingUserId,
      displayName: err.data.displayName,
      inviteStatus: err.data.inviteStatus
    };
  }
  if (err?.code === 'EMAIL_EXISTS') return { kind: 'exists', message: err.message };
  if (err?.code === 'ALREADY_IN_ROLE') return { kind: 'inRole', message: err.message };
  return { kind: 'error', message: err?.message || 'Failed to invite user' };
};

// What the reclaim will actually do, stated before the admin commits to it. An
// account that already accepted its invite keeps its password — the endpoint
// deliberately does not re-invite those — so promising a fresh link there would
// replace one misleading sentence with another.
const reclaimEffect = (inviteStatus) => (inviteStatus === 'accepted'
  ? 'They will be reactivated and assigned to this role. They already have a password, so no invitation is sent.'
  : 'They will be reactivated, assigned to this role, and sent a fresh invitation link.');

export default function InviteUserModal({ isOpen, roleName, onClose, onInvite, onResend, onReclaim, onAssignExisting }) {
  const [form, setForm] = useState(EMPTY_INVITE);
  const [busy, setBusy] = useState(false);
  const [outcome, setOutcome] = useState(null); // { kind, message, userId, displayName, inviteStatus }

  const setField = (key) => (e) => setForm((p) => ({ ...p, [key]: e.target.value }));

  const reset = () => { setForm(EMPTY_INVITE); setOutcome(null); };
  const close = () => { if (!busy) { reset(); onClose(); } };

  const submit = async () => {
    const { firstName, lastName, email } = form;
    if (!firstName.trim() || !lastName.trim() || !email.trim()) {
      return setOutcome({ kind: 'error', message: 'First name, last name and email are all required.' });
    }
    setBusy(true);
    setOutcome(null);
    try {
      const data = await onInvite(form);
      // A delivery failure is a 201, NOT an error — the account exists and the
      // token is valid, only the email failed. Never report that as success.
      setOutcome(data?.emailSent === false
        ? { kind: 'delivery', message: data.message, userId: data.user?.id }
        : { kind: 'success', message: `An invitation email is on its way to ${email.trim()}.` });
    } catch (err) {
      setOutcome(classifyError(err));
    } finally {
      setBusy(false);
    }
  };

  // Retry after a delivery failure = resend on the user that WAS created.
  // Re-submitting the form would collide with the row it just made (409).
  const retryDelivery = async () => {
    if (!outcome?.userId) return submit();
    setBusy(true);
    try {
      const data = await onResend(outcome.userId);
      setOutcome(data?.emailSent === false
        ? { kind: 'delivery', message: data.message, userId: outcome.userId }
        : { kind: 'success', message: `An invitation email is on its way to ${form.email.trim()}.` });
    } catch (err) {
      setOutcome({ kind: 'delivery', message: err?.message || 'Retry failed', userId: outcome.userId });
    } finally {
      setBusy(false);
    }
  };

  // Reclaim: ONE call does assign + reactivate + fresh invite. The manual remedy
  // was three separate admin actions, and skipping the middle one left the user
  // listed in the role but unable to log in.
  const reclaim = async () => {
    if (!outcome?.userId) return;
    setBusy(true);
    try {
      const data = await onReclaim(outcome.userId);
      // Same rule as invite: a failed email is a 200 carrying inviteSent:true +
      // emailSent:false. The row IS fixed — only the mail failed — so hand the
      // admin Resend rather than reporting the whole reclaim as a success.
      setOutcome(data?.inviteSent && data?.emailSent === false
        ? { kind: 'delivery', message: data.message, userId: outcome.userId }
        : { kind: 'success', message: data?.message || 'User reclaimed into this role.' });
    } catch (err) {
      setOutcome({ kind: 'error', message: err?.message || 'Failed to reclaim this account.' });
    } finally {
      setBusy(false);
    }
  };

  const meta = outcome ? OUTCOME[outcome.kind] : null;
  const done = outcome?.kind === 'success';
  const reclaimable = outcome?.kind === 'reclaim';

  // Exactly ONE primary action is offered at a time. A reclaimable conflict is
  // resolved by reclaiming; re-submitting the form would hit the identical 409,
  // so "Try Again" is deliberately not offered on that branch.
  const primaryAction = () => {
    if (done) return { label: 'Invite Another', onClick: reset };
    if (reclaimable) return { label: 'Reactivate and assign to this role', onClick: reclaim };
    return { label: outcome ? 'Try Again' : 'Send Invitation', onClick: submit };
  };
  const action = primaryAction();

  return (
    <Modal isOpen={isOpen} onClose={close} title={`Invite User to ${roleName || 'Role'}`}>
      {outcome && (
        <div className={`${styles.inviteAlert} ${styles[meta.cls]}`} role="status">
          <span className={styles.inviteAlertTitle}>{meta.title}</span>
          <span className={styles.inviteAlertText}>{outcome.message}</span>
          {reclaimable && (
            <span className={styles.inviteAlertText}>
              Reclaim <strong>{outcome.displayName || form.email.trim()}</strong> into {roleName || 'this role'}?{' '}
              {reclaimEffect(outcome.inviteStatus)}
            </span>
          )}
          {outcome.kind === 'exists' && (
            <Button size="small" variant="secondary" onClick={() => { const { email } = form; reset(); onAssignExisting(email.trim()); }}>
              Assign existing user to this role
            </Button>
          )}
          {outcome.kind === 'delivery' && (
            <Button size="small" variant="secondary" loading={busy} onClick={retryDelivery}>Retry sending</Button>
          )}
        </div>
      )}

      {/* On a reclaimable conflict the modal IS the confirm prompt — leaving the
          form up would let the admin edit the address while the button still
          points at the id the 409 named. */}
      {!done && !reclaimable && (
        <div className={styles.form}>
          <div className={styles.formRow}>
            <div className={styles.formGroup}><label className={styles.label}>First Name</label><Input value={form.firstName} onChange={setField('firstName')} placeholder="First name" /></div>
            <div className={styles.formGroup}><label className={styles.label}>Last Name</label><Input value={form.lastName} onChange={setField('lastName')} placeholder="Last name" /></div>
          </div>
          <div className={styles.formGroup}>
            <label className={styles.label}>Email</label>
            <Input type="email" value={form.email} onChange={setField('email')} placeholder="user@example.com" />
            <span className={styles.hint}>An invitation email with a set-password link will be sent. The link expires in 72 hours.</span>
          </div>
        </div>
      )}

      <div className={styles.formActions}>
        <Button variant="secondary" disabled={busy} onClick={close}>{done ? 'Done' : 'Cancel'}</Button>
        <Button loading={busy} onClick={action.onClick}>{action.label}</Button>
      </div>
    </Modal>
  );
}
